import { v4 as uuid } from 'uuid';

import { ActionId } from 'client/app/apps/disco-prototype/state/actions/actions';
import {
  applyComponentUpdate,
  Component,
  ComponentUpdate,
  LiquidSet,
} from 'client/app/apps/disco-prototype/state/liquidSet';
import { reorderItems } from 'client/app/apps/disco-prototype/state/reorder';
import {
  applyValueUpdate,
  Value,
  ValueUpdate,
} from 'client/app/apps/disco-prototype/state/table';
import { Error } from 'common/types/disco/calculateVolumes';
import { Component as CommonComponent } from 'common/types/disco/liquidSet';
import { queryTable } from 'common/types/disco/table';
import { ColumnValue } from 'common/types/disco/value';

export type MakeMixtureAction = {
  id: ActionId;
  type: 'makeMixture';
  title: string;
  source?: LiquidSet;
  additions: LiquidAddition[];
  isConstrained: boolean;
  finalVolume: Value;
  finalComposition: Component[];
  compiled?: {
    // the final volumes of the mixture as calculated by the backend
    finalVolume?: ColumnValue;
    // finalComposition the final concentrations as calculated by the backend
    finalComposition?: CommonComponent[];
    // any errors encountered when applying the constraints
    errors?: Error[];
  };
};

export const getDefaultMakeMixture = (): MakeMixtureAction => ({
  id: uuid(),
  type: 'makeMixture',
  title: 'Liquid Handling',
  additions: [],
  isConstrained: false,
  finalVolume: {
    selectedMode: 'const',
    value: '10',
  },
  finalComposition: [],
});

export type MakeMixtureActionUpdate = {
  type: 'makeMixture';
  title?: string;
  source?: string;
  additions?: LiquidAdditionUpdate;
  isConstrained?: boolean;
  finalVolume?: ValueUpdate;
  finalComposition?: ComponentUpdate;
};

export const applyMakeMixtureActionUpdate = (
  action: MakeMixtureAction,
  liquidSets: LiquidSet[],
  target: LiquidSet | undefined,
  update: MakeMixtureActionUpdate,
) => {
  action.type = update.type ?? action.type;
  action.title = update.title ?? action.title;
  if (update.source) {
    action.source = liquidSets.find(ls => ls.id === update.source) ?? action.source;
  }
  applyLiquidAdditionsUpdate(action, liquidSets, update?.additions);
  action.isConstrained = update.isConstrained ?? action.isConstrained;
  applyValueUpdate(action.finalVolume, update.finalVolume);
  action.finalComposition = applyComponentUpdate(
    action.finalComposition,
    update.finalComposition,
    action.source,
    target,
  );
};

type AdditionMode = 'volume' | 'concentration' | 'diluent';

export type LiquidAdditionId = string;

export type LiquidAddition = {
  id: LiquidAdditionId;
  sourceName: string;
  mode: AdditionMode;
  volume: Value;
  compiled?: {
    // actual volume transferred, reference to a column in the compiled
    // target table
    volume: ColumnValue;
    // Index of the selected source liquid in the source liquid set.
    // Reference to a column in the compiled target table.
    index: ColumnValue;
    // errors encountered with this addition
    errors?: Error[];
  };
};

const getDefaultLiquidAddition = (
  source?: LiquidSet,
  prevAddition?: LiquidAddition,
): LiquidAddition => {
  // If there was a previous addition, the next one should by default have
  //  - the next unique name
  //  - the same addition mode
  // This makes it easier for the user to add large numbers of additions

  const nameColumn = source?.liquidProps.name.selectedColumnName;
  const uniqueLiquidNames = nameColumn
    ? [...new Set(queryTable(source?.table).findColumn(nameColumn) ?? [])]
    : [];
  const nextIndex =
    (uniqueLiquidNames.findIndex(n => n === prevAddition?.sourceName) + 1) %
    uniqueLiquidNames.length;
  const name = uniqueLiquidNames[nextIndex] ?? '';

  const mode = prevAddition?.mode ?? 'volume';

  return {
    id: uuid(),
    sourceName: name,
    mode,
    volume: {
      selectedMode: 'const',
      value: '10.0',
    },
  };
};

type LiquidAdditionUpdate =
  | CreateLiquidAddition
  | UpdateLiquidAddition
  | ReorderLiquidAdditions
  | DeleteLiquidAddition;

type CreateLiquidAddition = {
  type: 'create';
  name: string;
};

type UpdateLiquidAddition = {
  type: 'update';
  toUpdate: LiquidAdditionId;
  sourceName?: string;
  mode?: AdditionMode;
  volume?: ValueUpdate;
};

type ReorderLiquidAdditions = {
  type: 'reorder';
  newOrder: LiquidAdditionId[];
};

type DeleteLiquidAddition = {
  type: 'delete';
  toDelete: LiquidAdditionId;
};

const applyLiquidAdditionsUpdate = (
  action: MakeMixtureAction,
  liquidSets: LiquidSet[],
  update?: LiquidAdditionUpdate,
) => {
  if (!update) return;
  switch (update.type) {
    case 'create': {
      const prevAddition = action.additions[action.additions.length - 1];
      const newAddition = getDefaultLiquidAddition(action.source, prevAddition);
      action.additions.push(newAddition);
      break;
    }
    case 'update': {
      const toUpdate = action.additions.find(a => a.id === update.toUpdate);
      if (toUpdate) {
        toUpdate.sourceName = update.sourceName ?? toUpdate.sourceName;
        toUpdate.mode = update.mode ?? toUpdate.mode;
        applyValueUpdate(toUpdate.volume, update.volume);
      }
      break;
    }
    case 'reorder': {
      action.additions = reorderItems(action.additions, update.newOrder);
      break;
    }
    case 'delete': {
      action.additions = action.additions.filter(a => a.id !== update.toDelete);
      break;
    }
    default: {
      const _: never = update;
      console.error(`unexpected liquid addition update ${_}`);
    }
  }
};
